// src/contexts/AccountContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import useAuthReset from '../hooks/useAuthReset';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://soldikeeper-backend-production.up.railway.app/api';

// Replicate the same createAuthApi pattern used in BudgetContext
const createAuthApi = () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-auth-token': token,
    },
  });
};

const AccountContext = createContext({
  accounts: [],
  defaultAccount: null,
  loading: false,
  error: null,
  limitReached: false,
  createAccount: async () => {},
  updateAccount: async () => {},
  deleteAccount: async () => {},
  refreshAccounts: async () => {},
  setAsDefaultAccount: async () => {},
});

export const AccountProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?._id ?? user?.id ?? null;

  const [accounts, setAccounts] = useState([]);
  const [defaultAccount, setDefaultAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limitReached, setLimitReached] = useState(false);

  const refreshAccounts = useCallback(async () => {
    // Bail out if we have no authenticated user — prevents stale fetches
    // racing against a logout that just removed the token.
    if (!localStorage.getItem('token')) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const api = createAuthApi();
      const response = await api.get('/accounts');
      const data = response.data || [];
      setAccounts(data);
      setDefaultAccount(data.find(a => a.isDefault) || data[0] || null);
      setLimitReached(false);
    } catch (err) {
      if (
        err.response?.status === 403 &&
        err.response?.data?.code === 'USAGE_LIMIT_EXCEEDED'
      ) {
        setLimitReached(true);
      } else if (err.response?.status !== 401) {
        // Don't set error on 401 — auth context handles that
        setError(err.response?.data?.message || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Privacy guard: clear cached accounts the moment the authenticated
  // user changes (logout, login as a different user, or 401-forced
  // logout). This prevents User A's accounts from being visible to
  // User B on a shared browser/device. See useAuthReset for details.
  const resetAccountState = useCallback(() => {
    setAccounts([]);
    setDefaultAccount(null);
    setError(null);
    setLimitReached(false);
  }, []);
  useAuthReset(resetAccountState);

  useEffect(() => {
    if (userId) {
      refreshAccounts();
    } else {
      // Logged out → wipe state and stop loading.
      setAccounts([]);
      setError(null);
      setLimitReached(false);
      setLoading(false);
    }
  }, [userId, refreshAccounts]);

  // Re-fetch when BudgetContext signals a transaction with an accountId was saved
  useEffect(() => {
    const handler = () => refreshAccounts();
    window.addEventListener('soldikeeper:accounts-refresh', handler);
    return () => window.removeEventListener('soldikeeper:accounts-refresh', handler);
  }, [refreshAccounts]);

  const createAccount = useCallback(async (data) => {
    const api = createAuthApi();
    const response = await api.post('/accounts', data);
    await refreshAccounts();
    return response.data;
  }, [refreshAccounts]);

  const updateAccount = useCallback(async (id, data) => {
    const api = createAuthApi();
    const response = await api.put(`/accounts/${id}`, data);
    await refreshAccounts();
    return response.data;
  }, [refreshAccounts]);

  const deleteAccount = useCallback(async (id) => {
    const api = createAuthApi();
    await api.delete(`/accounts/${id}`);
    await refreshAccounts();
  }, [refreshAccounts]);

  const setAsDefaultAccount = useCallback(async (id) => {
    const api = createAuthApi();
    await api.put(`/accounts/${id}/default`);
    await refreshAccounts();
  }, [refreshAccounts]);

  return (
    <AccountContext.Provider
      value={{
        accounts,
        defaultAccount,
        loading,
        error,
        limitReached,
        createAccount,
        updateAccount,
        deleteAccount,
        refreshAccounts,
        setAsDefaultAccount,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => useContext(AccountContext);
export { AccountContext };
export default AccountContext;
